import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserCircle, Package, Car, Wrench, Storefront, DotsThreeCircle, Plus, X, Camera,
  SignIn, SignOut, ArrowRight, Buildings, Key, Copy, Check, Trash, ShareNetwork,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtime } from "../lib/realtime";
import CameraCapture from "../components/CameraCapture";
import { VISITOR_TYPES, MERCHANTS, VISITOR_STATUS, typeMeta } from "../lib/visitors";
import { GateHelp, ResidentHelp } from "./DailyHelp";

function ModeToggle({ mode, setMode }) {
  return (
    <div className="flex gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5" data-testid="visitor-mode">
      {[["visitors", "Visitors"], ["help", "Daily Help"]].map(([k, label]) => (
        <button key={k} onClick={() => setMode(k)} data-testid={`mode-${k}`}
          className={`flex-1 rounded-xl px-2 py-2 text-[13px] font-bold transition ${mode === k ? "bg-neon-cyan text-black shadow-glow-cyan" : "text-white/65 hover:bg-white/5"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

const ICONS = { UserCircle, Package, Car, Wrench, Storefront, DotsThreeCircle };
const COLOR = { cyan: "text-neon-cyan", lime: "text-neon-lime", amber: "text-neon-amber", pink: "text-neon-pink", white: "text-white/70" };
const CHIP = {
  amber: "border-neon-amber/30 bg-neon-amber/10 text-neon-amber",
  lime: "border-neon-lime/30 bg-neon-lime/10 text-neon-lime",
  pink: "border-neon-pink/30 bg-neon-pink/10 text-neon-pink",
  cyan: "border-neon-cyan/30 bg-neon-cyan/10 text-neon-cyan",
  white: "border-white/15 bg-white/5 text-white/70",
};

export default function Visitors() {
  const { user } = useAuth();
  const isGate = ["MANAGER", "CHAIRPERSON", "ADMIN", "SECURITY"].includes(user?.role);
  return isGate ? <GateConsole /> : <MyVisitors flatLabel={user?.flat?.label} />;
}

/* ───────────────────────── Security / office gate console ───────────────────────── */
function GateConsole() {
  const { subscribe } = useRealtime();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("PENDING"); // PENDING | INSIDE | ALL
  const [mode, setMode] = useState("visitors"); // visitors | help
  const [logOpen, setLogOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/api/visitors?scope=today").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
  useEffect(() => subscribe((m) => {
    if (m.type === "notification" && String(m.data?.kind || "").startsWith("VISITOR")) load();
  }), [subscribe, load]);

  const buckets = useMemo(() => ({
    PENDING: items.filter((v) => v.status === "PENDING"),
    INSIDE: items.filter((v) => v.status === "CHECKED_IN"),
    ALL: items,
  }), [items]);
  const view = buckets[tab] || [];

  return (
    <div className="space-y-5 font-sans" data-testid="visitors-gate">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Gate</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Visitors</h1>
          <p className="mt-1 text-sm text-white/55">Log entries, get resident approval, track who is inside.</p>
        </div>
        {mode === "visitors" && (
          <div className="flex gap-2">
            <button onClick={() => setVerifyOpen(true)} data-testid="verify-code-btn"
              className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm font-bold text-white/85 active:scale-95">
              <Key size={16} weight="bold" /> Verify code
            </button>
            <button onClick={() => setLogOpen(true)} data-testid="log-visitor-btn"
              className="inline-flex items-center gap-2 rounded-2xl bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan active:scale-95">
              <Plus size={16} weight="bold" /> Log visitor
            </button>
          </div>
        )}
      </div>

      <ModeToggle mode={mode} setMode={setMode} />

      {mode === "help" ? <GateHelp /> : (
        <>
          <div className="flex gap-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5">
            {[["PENDING", `Awaiting (${buckets.PENDING.length})`], ["INSIDE", `Inside (${buckets.INSIDE.length})`], ["ALL", `Today (${buckets.ALL.length})`]].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`vtab-${k}`}
                className={`flex-1 rounded-xl px-2 py-2 text-[12.5px] font-bold transition ${tab === k ? "bg-neon-cyan text-black shadow-glow-cyan" : "text-white/65 hover:bg-white/5"}`}>
                {label}
              </button>
            ))}
          </div>
          {view.length === 0 ? (
            <div className="card p-8 text-center text-sm text-white/45">No visitors {tab === "ALL" ? "logged today" : tab === "INSIDE" ? "inside" : "awaiting approval"}.</div>
          ) : (
            <div className="space-y-2.5">
              {view.map((v) => <GateCard key={v.id} v={v} onChange={load} />)}
            </div>
          )}
        </>
      )}

      <AnimatePresence>
        {logOpen && <LogVisitorSheet onClose={() => setLogOpen(false)} onLogged={() => { setLogOpen(false); setTab("PENDING"); load(); }} />}
        {verifyOpen && <VerifyCodeModal onClose={() => setVerifyOpen(false)} onVerified={() => { setVerifyOpen(false); setTab("INSIDE"); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function VerifyCodeModal({ onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  async function submit() {
    if (code.length < 4) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/api/visitors/verify-code", { code });
      setOk(data);
      setTimeout(onVerified, 1400);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d === "code_expired" ? "This code has expired." : d === "invalid_code" ? "No active guest for that code." : "Could not verify. Try again.");
      setBusy(false);
    }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        className="relative w-full max-w-sm rounded-[2rem] border border-white/10 bg-ink-900 p-6 text-center" data-testid="verify-code-modal">
        {ok ? (
          <div className="py-4">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-neon-lime/15 text-neon-lime"><Check size={30} weight="bold" /></div>
            <p className="font-display text-lg font-bold text-white">{ok.name} checked in</p>
            <p className="text-[13px] text-white/55">{ok.flatLabel} · entry recorded</p>
          </div>
        ) : (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Key size={22} weight="duotone" /></div>
            <h3 className="font-display text-xl font-bold text-white">Verify gate code</h3>
            <p className="mt-1 text-[13px] text-white/55">Enter the 6-digit code the resident shared.</p>
            <input autoFocus inputMode="numeric" maxLength={6} value={code} data-testid="verify-code-input"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="mt-4 w-full rounded-2xl border border-white/15 bg-white/[0.04] py-4 text-center font-mono text-3xl font-extrabold tracking-[0.4em] text-white outline-none focus:border-neon-cyan" placeholder="••••••" />
            {err && <p className="mt-2 text-sm text-neon-pink">{err}</p>}
            <div className="mt-4 flex gap-2.5">
              <button onClick={onClose} className="flex-1 rounded-2xl border border-white/15 bg-white/5 py-3 text-sm font-bold text-white/80">Cancel</button>
              <button disabled={code.length < 4 || busy} onClick={submit} data-testid="verify-code-submit"
                className="flex-1 rounded-2xl bg-neon-cyan py-3 text-sm font-bold text-black shadow-glow-cyan disabled:opacity-50">{busy ? "Checking…" : "Verify"}</button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function GateCard({ v, onChange }) {
  const meta = typeMeta(v.type);
  const Icon = ICONS[meta.icon] || UserCircle;
  const st = VISITOR_STATUS[v.status] || { label: v.status, color: "white" };
  const [busy, setBusy] = useState(false);

  async function act(path) {
    setBusy(true);
    try { await api.post(`/api/visitors/${v.id}/${path}`); onChange(); }
    catch {} finally { setBusy(false); }
  }

  return (
    <div className="card flex items-center gap-3 p-3.5" data-testid={`gate-card-${v.id}`}>
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-white/[0.05]">
        {v.photoUrl
          ? <img src={v.photoUrl} alt="" className="h-full w-full object-cover" />
          : <span className={`flex h-full w-full items-center justify-center ${COLOR[meta.accent]}`}><Icon size={22} weight="duotone" /></span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-display text-[15px] font-semibold text-white">{v.name}</p>
          <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${CHIP[st.color]}`}>{st.label}</span>
        </div>
        <p className="truncate text-[12px] text-white/55">
          {meta.label}{v.merchant ? ` · ${v.merchant}` : ""} → <span className="font-mono text-white/80">{v.flatLabel}</span>
          {v.phone ? ` · ${v.phone}` : ""}
        </p>
        {v.status === "DENIED" && <p className="text-[11px] text-neon-pink">Denied by {v.decidedByName || "resident"}</p>}
      </div>
      <div className="flex shrink-0 flex-col gap-1.5">
        {(v.status === "APPROVED" || v.status === "PENDING") && (
          <button disabled={busy} onClick={() => act("checkin")} data-testid={`checkin-${v.id}`}
            className="inline-flex items-center gap-1 rounded-xl bg-neon-lime/15 px-3 py-1.5 text-[12px] font-bold text-neon-lime disabled:opacity-50">
            <SignIn size={14} weight="bold" /> Entry
          </button>
        )}
        {v.status === "CHECKED_IN" && (
          <button disabled={busy} onClick={() => act("checkout")} data-testid={`checkout-${v.id}`}
            className="inline-flex items-center gap-1 rounded-xl bg-white/5 px-3 py-1.5 text-[12px] font-bold text-white/80 disabled:opacity-50">
            <SignOut size={14} weight="bold" /> Exit
          </button>
        )}
      </div>
    </div>
  );
}

function LogVisitorSheet({ onClose, onLogged }) {
  const [type, setType] = useState("GUEST");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [merchant, setMerchant] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [flat, setFlat] = useState(null);
  const [photoKey, setPhotoKey] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const merchants = MERCHANTS[type];
  const valid = name.trim().length >= 2 && flat;

  async function onCapture(b64) {
    setCamOpen(false);
    try {
      const blob = await (await fetch(`data:image/jpeg;base64,${b64}`)).blob();
      const fd = new FormData(); fd.append("file", blob, "visitor.jpg");
      const { data } = await api.post("/api/visitors/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoKey(data.photoKey); setPhotoUrl(data.photoUrl);
    } catch { setErr("Photo upload failed (skip and continue)."); }
  }

  async function submit() {
    if (!valid) { setErr("Enter a name and pick a flat."); return; }
    setBusy(true); setErr(null);
    try {
      await api.post("/api/visitors", {
        type, name: name.trim(), flatId: flat.id, phone: phone || null,
        merchant: merchant || null, vehicleNumber: vehicle || null,
        purpose: purpose || null, photoKey,
      });
      onLogged();
    } catch { setErr("Could not log visitor. Try again."); setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="log-visitor-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Log a visitor</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>

        <div className="mt-4 space-y-4">
          {/* Type */}
          <div className="grid grid-cols-3 gap-2">
            {VISITOR_TYPES.map((t) => {
              const I = ICONS[t.icon] || UserCircle; const on = type === t.code;
              return (
                <button key={t.code} onClick={() => { setType(t.code); setMerchant(""); }} data-testid={`vtype-${t.code}`}
                  className={`flex flex-col items-center gap-1 rounded-2xl border px-2 py-2.5 text-[12px] font-semibold transition ${on ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/65"}`}>
                  <I size={20} weight={on ? "fill" : "duotone"} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Merchant suggestions */}
          {merchants && (
            <div>
              <label className="label">{type === "SERVICE" ? "Service" : type === "CAB" ? "Cab service" : "Company"}</label>
              <div className="flex flex-wrap gap-1.5">
                {merchants.map((m) => (
                  <button key={m} onClick={() => setMerchant(m === "Other" ? "" : m)}
                    className={`chip ${merchant === m ? "border-neon-cyan/50 text-neon-cyan" : ""}`}>{m}</button>
                ))}
              </div>
              <input className="field mt-2" placeholder="Or type a name" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
            </div>
          )}

          <div>
            <label className="label">Visitor name</label>
            <input className="field" data-testid="visitor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh / Delivery agent" />
          </div>

          <FlatPicker value={flat} onChange={setFlat} />

          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Phone (opt)</label>
              <input className="field" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit" /></div>
            <div><label className="label">Vehicle (opt)</label>
              <input className="field uppercase" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="MH12 AB 1234" /></div>
          </div>

          <div>
            <label className="label">Purpose (opt)</label>
            <input className="field" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Meeting, package drop" />
          </div>

          {/* Photo */}
          <div className="flex items-center gap-3">
            {photoUrl
              ? <img src={photoUrl} alt="" className="h-14 w-14 rounded-2xl object-cover" />
              : <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.05] text-white/40"><Camera size={20} weight="duotone" /></div>}
            <button onClick={() => setCamOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-[12px] font-bold text-white/80">
              <Camera size={14} weight="bold" /> {photoUrl ? "Retake photo" : "Capture photo (opt)"}
            </button>
          </div>

          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}

          <button disabled={!valid || busy} onClick={submit} data-testid="visitor-submit"
            className="btn-primary w-full disabled:opacity-50">
            {busy ? "Notifying resident…" : <>Notify {flat?.label || "resident"} <ArrowRight weight="bold" className="ml-1" /></>}
          </button>
        </div>
      </motion.div>
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={onCapture} label="Visitor photo" />
    </motion.div>
  );
}

function FlatPicker({ value, onChange }) {
  const [flats, setFlats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/api/flats").then(({ data }) => setFlats(data.items || [])).catch(() => {}); }, []);
  const matches = useMemo(() => {
    const s = q.trim().toUpperCase();
    if (!s) return flats.slice(0, 8);
    return flats.filter((f) => (f.label || "").toUpperCase().includes(s)).slice(0, 12);
  }, [q, flats]);
  return (
    <div className="relative">
      <label className="label">Visiting flat</label>
      <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.03] px-3">
        <Buildings size={16} className="text-neon-cyan" />
        <input className="flex-1 bg-transparent py-3 text-white outline-none" data-testid="flat-search"
          placeholder={value ? value.label : "Search flat e.g. C-1908"}
          value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
        {value && <span className="rounded-md bg-neon-cyan/15 px-2 py-0.5 font-mono text-[12px] font-bold text-neon-cyan">{value.label}</span>}
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-2xl border border-white/10 bg-ink-800 p-1 shadow-xl">
          {matches.map((f) => (
            <button key={f.id} onClick={() => { onChange(f); setQ(""); setOpen(false); }} data-testid={`flat-opt-${f.id}`}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[13px] text-white hover:bg-white/[0.05]">
              <span className="font-mono font-bold text-neon-cyan">{f.label}</span>
              {f.residents?.[0] && <span className="text-white/50">· {f.residents[0].name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Resident "My Visitors" ───────────────────────── */
function MyVisitors({ flatLabel }) {
  const [items, setItems] = useState([]);
  const [expected, setExpected] = useState([]);
  const [expOpen, setExpOpen] = useState(false);
  const [mode, setMode] = useState("visitors"); // visitors | help
  const load = useCallback(() => {
    api.get("/api/visitors").then(({ data }) => setItems(data.items || [])).catch(() => {});
    api.get("/api/visitors/expected").then(({ data }) => setExpected(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  const pending = items.filter((v) => v.status === "PENDING");

  return (
    <div className="space-y-5 font-sans" data-testid="my-visitors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">{flatLabel || "Your flat"}</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Visitors</h1>
          <p className="mt-1 text-sm text-white/55">Approvals, expected guests, and daily help.</p>
        </div>
        {mode === "visitors" && (
          <button onClick={() => setExpOpen(true)} data-testid="expected-btn"
            className="inline-flex items-center gap-2 rounded-2xl bg-neon-lime px-4 py-2.5 text-sm font-bold text-black shadow-glow-lime active:scale-95">
            <Key size={16} weight="bold" /> Pre-approve
          </button>
        )}
      </div>

      <ModeToggle mode={mode} setMode={setMode} />

      {mode === "help" ? <ResidentHelp /> : <>

      {pending.length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-neon-amber">Awaiting your approval</p>
          {pending.map((v) => <ApprovalCard key={v.id} v={v} onDone={load} />)}
        </div>
      )}

      {expected.length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan">Expected guests · share the code</p>
          {expected.map((v) => <ExpectedRow key={v.id} v={v} onChange={load} />)}
        </div>
      )}

      <div className="space-y-2.5">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Recent</p>
        {items.length === 0 ? <div className="card p-6 text-center text-sm text-white/45">No visitors yet.</div>
          : items.map((v) => <HistoryRow key={v.id} v={v} />)}
      </div>
      </>}

      <AnimatePresence>
        {expOpen && <ExpectedGuestSheet onClose={() => setExpOpen(false)} onCreated={() => { setExpOpen(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function ExpectedRow({ v, onChange }) {
  const meta = typeMeta(v.type); const Icon = ICONS[meta.icon] || UserCircle;
  const [copied, setCopied] = useState(false);
  function copy() {
    try { navigator.clipboard?.writeText(v.code || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  }
  function share() {
    const msg = `Your gate code for ${v.flatLabel} (Society Plus) is ${v.code}. Show it at the gate.`;
    if (navigator.share) navigator.share({ text: msg }).catch(() => {});
    else copy();
  }
  async function cancel() { await api.post(`/api/visitors/${v.id}/cancel`); onChange(); }
  return (
    <div className="card p-4" data-testid={`expected-${v.id}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.05] ${COLOR[meta.accent]}`}><Icon size={22} weight="duotone" /></div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-[15px] font-semibold text-white">{v.name}</p>
          <p className="text-[12px] text-white/55">{meta.label}{v.merchant ? ` · ${v.merchant}` : ""}</p>
        </div>
        <button onClick={cancel} className="rounded-full bg-white/5 p-2 text-white/55 hover:text-neon-pink" title="Cancel"><Trash size={15} weight="bold" /></button>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 rounded-2xl border border-neon-cyan/30 bg-neon-cyan/[0.07] py-2.5 text-center font-mono text-2xl font-extrabold tracking-[0.35em] text-neon-cyan">{v.code}</div>
        <button onClick={copy} className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-white/80">{copied ? <Check size={18} weight="bold" /> : <Copy size={18} weight="bold" />}</button>
        <button onClick={share} className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-white/80"><ShareNetwork size={18} weight="bold" /></button>
      </div>
    </div>
  );
}

function ExpectedGuestSheet({ onClose, onCreated }) {
  const [type, setType] = useState("GUEST");
  const [name, setName] = useState("");
  const [merchant, setMerchant] = useState("");
  const [validDays, setValidDays] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const merchants = MERCHANTS[type];
  const valid = name.trim().length >= 2;
  async function submit() {
    if (!valid) { setErr("Enter a name."); return; }
    setBusy(true); setErr(null);
    try { await api.post("/api/visitors/expected", { type, name: name.trim(), merchant: merchant || null, validDays }); onCreated(); }
    catch { setErr("Could not create. Try again."); setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-lg overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="expected-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15 sm:hidden" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Pre-approve a guest</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {VISITOR_TYPES.map((t) => {
              const I = ICONS[t.icon] || UserCircle; const on = type === t.code;
              return (
                <button key={t.code} onClick={() => { setType(t.code); setMerchant(""); }}
                  className={`flex flex-col items-center gap-1 rounded-2xl border px-2 py-2.5 text-[12px] font-semibold transition ${on ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/65"}`}>
                  <I size={20} weight={on ? "fill" : "duotone"} /> {t.label}
                </button>
              );
            })}
          </div>
          {merchants && (
            <div>
              <label className="label">{type === "SERVICE" ? "Service" : type === "CAB" ? "Cab service" : "Company"}</label>
              <div className="flex flex-wrap gap-1.5">
                {merchants.map((m) => <button key={m} onClick={() => setMerchant(m === "Other" ? "" : m)} className={`chip ${merchant === m ? "border-neon-cyan/50 text-neon-cyan" : ""}`}>{m}</button>)}
              </div>
            </div>
          )}
          <div><label className="label">Guest name</label>
            <input className="field" data-testid="expected-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cousin Rohit" /></div>
          <div>
            <label className="label">Valid for</label>
            <div className="flex gap-2">
              {[[1, "Today"], [2, "2 days"], [7, "A week"]].map(([d, l]) => (
                <button key={d} onClick={() => setValidDays(d)} className={`chip ${validDays === d ? "border-neon-cyan/50 text-neon-cyan" : ""}`}>{l}</button>
              ))}
            </div>
          </div>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} data-testid="expected-submit" className="btn-primary w-full disabled:opacity-50">
            {busy ? "Generating code…" : <>Generate gate code <ArrowRight weight="bold" className="ml-1" /></>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function HistoryRow({ v }) {
  const meta = typeMeta(v.type); const Icon = ICONS[meta.icon] || UserCircle;
  const st = VISITOR_STATUS[v.status] || { label: v.status, color: "white" };
  return (
    <div className="card flex items-center gap-3 p-3.5">
      <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-2xl bg-white/[0.05]">
        {v.photoUrl ? <img src={v.photoUrl} alt="" className="h-full w-full object-cover" />
          : <span className={`flex h-full w-full items-center justify-center ${COLOR[meta.accent]}`}><Icon size={20} weight="duotone" /></span>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-[14px] font-semibold text-white">{v.name}</p>
        <p className="truncate text-[12px] text-white/55">{meta.label}{v.merchant ? ` · ${v.merchant}` : ""}{v.checkInAt ? ` · in ${fmt(v.checkInAt)}` : ""}{v.checkOutAt ? ` · out ${fmt(v.checkOutAt)}` : ""}</p>
      </div>
      <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${CHIP[st.color]}`}>{st.label}</span>
    </div>
  );
}

/* Shared approval card (used inline for residents and by the popup listener) */
export function ApprovalCard({ v, onDone, compact }) {
  const meta = typeMeta(v.type); const Icon = ICONS[meta.icon] || UserCircle;
  const [busy, setBusy] = useState(false);
  async function decide(decision) {
    setBusy(true);
    try { await api.post(`/api/visitors/${v.id}/decision`, { decision }); onDone?.(decision); }
    catch { setBusy(false); }
  }
  return (
    <div className={`card p-4 ${compact ? "" : ""}`} data-testid={`approval-${v.id}`}>
      <div className="flex items-center gap-3">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-white/[0.05]">
          {v.photoUrl ? <img src={v.photoUrl} alt="" className="h-full w-full object-cover" />
            : <span className={`flex h-full w-full items-center justify-center ${COLOR[meta.accent]}`}><Icon size={28} weight="duotone" /></span>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-bold text-white">{v.name}</p>
          <p className="text-[13px] text-white/60">{meta.label}{v.merchant ? ` · ${v.merchant}` : ""}</p>
          <p className="mt-0.5 text-[12px] text-white/45">Visiting <span className="font-mono text-white/70">{v.flatLabel}</span>{v.phone ? ` · ${v.phone}` : ""}{v.purpose ? ` · ${v.purpose}` : ""}</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2.5">
        <button disabled={busy} onClick={() => decide("DENY")} data-testid={`deny-${v.id}`}
          className="flex-1 rounded-2xl border border-neon-pink/30 bg-neon-pink/10 py-3 text-sm font-bold text-neon-pink disabled:opacity-50">Deny</button>
        <button disabled={busy} onClick={() => decide("APPROVE")} data-testid={`approve-${v.id}`}
          className="flex-1 rounded-2xl bg-neon-lime py-3 text-sm font-bold text-black shadow-glow-lime disabled:opacity-50">{busy ? "…" : "Approve"}</button>
      </div>
    </div>
  );
}

function fmt(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}
